import { PageHeader, SectionLabel, Card, Stat, SampleBanner, StatusPill, Row } from '../components/ui.js';

interface Invoice {
  id: string;
  number: string;
  date: string;
  amount: number;
  status: 'paid' | 'open' | 'void';
}

const INVOICES: Invoice[] = [
  { id: 'inv-009', number: 'BLOOM-2026-009', date: '2026-04-25', amount: 649, status: 'paid' },
  { id: 'inv-008', number: 'BLOOM-2026-008', date: '2026-03-25', amount: 649, status: 'paid' },
  { id: 'inv-007', number: 'BLOOM-2026-007', date: '2026-02-25', amount: 649, status: 'paid' },
  { id: 'inv-006', number: 'BLOOM-2026-006', date: '2026-01-25', amount: 649, status: 'paid' },
  { id: 'inv-005', number: 'BLOOM-2026-005', date: '2025-12-25', amount: 649, status: 'paid' },
  { id: 'inv-004', number: 'BLOOM-2026-004', date: '2025-11-25', amount: 649, status: 'paid' },
];

export function BillingPage() {
  const totalPaid = INVOICES.filter((i) => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const nextBilling = '2026-05-25';

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Billing"
        title={<>Plan, payment, <em className="text-cream/80">invoices.</em></>}
        subtitle="Bloom Pro covers up to 3 locations, full content + GBP + AIEO + audits + rank tracking. Cancel any time, no contract."
      />
      <SampleBanner />

      <section>
        <SectionLabel>Current plan</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40">Plan</div>
                <div className="font-serif text-3xl text-cream tracking-tightest mt-1">Bloom Pro</div>
                <div className="text-[11px] text-cream/55 mt-1">Up to 3 locations · monthly · cancel any time</div>
              </div>
              <StatusPill tone="ok">active</StatusPill>
            </div>
            <div className="font-serif text-5xl text-cream tracking-tightest tabular-nums mt-4">$649<span className="text-2xl text-cream/45 ml-1">/mo</span></div>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-cream/75"><span className="text-emerald-300/85">✓</span> Content engine (12 posts/mo)</div>
              <div className="flex items-center gap-2 text-cream/75"><span className="text-emerald-300/85">✓</span> GBP posts + review replies</div>
              <div className="flex items-center gap-2 text-cream/75"><span className="text-emerald-300/85">✓</span> Weekly 25-point audits</div>
              <div className="flex items-center gap-2 text-cream/75"><span className="text-emerald-300/85">✓</span> AIEO across 4 platforms</div>
              <div className="flex items-center gap-2 text-cream/75"><span className="text-emerald-300/85">✓</span> Rank tracking (25 keywords)</div>
              <div className="flex items-center gap-2 text-cream/75"><span className="text-emerald-300/85">✓</span> Daily site QA + backups</div>
            </div>
          </Card>

          <Card>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-3">Payment method</div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-7 rounded bg-gradient-to-br from-cream/20 to-cream/[0.05] border border-cream/15 flex items-center justify-center text-[9px] uppercase tracking-wider text-cream/85 font-semibold">Visa</div>
              <span className="text-cream/85 tabular-nums">•••• 4242</span>
            </div>
            <div className="text-[11px] text-cream/45">Expires 11/2028</div>
            <button type="button" className="mt-6 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-cream/70 hover:text-cream border border-cream/[0.12] hover:border-cream/[0.3] transition-colors">
              Update card
            </button>
          </Card>
        </div>
      </section>

      <section>
        <SectionLabel>Account</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Total paid" value={`$${totalPaid.toLocaleString()}`} hint="lifetime" />
          <Stat label="Months active" value={INVOICES.length} hint="started Nov 2025" />
          <Stat label="Locations" value={3} hint="of 3 included" tone="ok" />
          <Stat label="Next charge" value={new Date(nextBilling).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} hint={`$649 to •••• 4242`} />
        </div>
      </section>

      <section>
        <SectionLabel>Invoice history</SectionLabel>
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                <th className="px-5 py-3 font-medium">Invoice</th>
                <th className="px-3 py-3 font-medium">Date</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium text-right">Amount</th>
                <th className="px-5 py-3 font-medium text-right">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {INVOICES.map((i) => (
                <tr key={i.id} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                  <td className="px-5 py-3 text-cream/85 font-mono text-[12px]">{i.number}</td>
                  <td className="px-3 py-3 text-cream/70">{new Date(i.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                  <td className="px-3 py-3"><StatusPill tone={i.status === 'paid' ? 'ok' : i.status === 'open' ? 'warn' : 'neutral'}>{i.status}</StatusPill></td>
                  <td className="px-3 py-3 text-right tabular-nums text-cream/85">${i.amount}</td>
                  <td className="px-5 py-3 text-right">
                    <a className="text-[11px] uppercase tracking-[0.18em] text-cream/55 hover:text-cream transition-colors" href="#" onClick={(e) => e.preventDefault()}>
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <SectionLabel>Plan</SectionLabel>
        <Card>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
            <Row label="Billing cycle">Monthly · 25th of each month</Row>
            <Row label="Cancellation">Anytime · effective end of cycle</Row>
            <Row label="Support">team@holocronhq.com</Row>
          </dl>
        </Card>
      </section>
    </div>
  );
}
